/**
 * Builds the first context prompt that the RAP Discovery chat receives.
 * It carries the OCR data and the full list of RAP strategies. 868kwhcbp.
 */

export const WORKING_SET_CONTEXT_TYPE = 'RAP_DISCOVERY_WORKING_SET';
export const WORKING_SET_SCHEMA_VERSION = 1;
/** The Discovery screen shows this many strategies. */
export const TOP_FIVE_COUNT = 5;

/** Reads a number from a value such as 6000, "6000" or "$6,000". Returns 0 when it holds no number. */
export function toNumericAmount(value: any): number {
  if (typeof value === 'number') {
    return isFinite(value) ? value : 0;
  }
  const parsed = Number(String(value ?? '').replace(/[$,]/g, ''));
  return isFinite(parsed) ? parsed : 0;
}

/**
 * Orders strategies the way the Discovery Top 5 shows them: by totalSavings,
 * highest first. A missing totalSavings counts as 0. Equal values keep their
 * input order. The input array is not changed.
 */
export function rankByDisplayedTop5<T extends { totalSavings?: any }>(list: T[] | null | undefined): T[] {
  return (list || [])
    .map((item, index) => ({ item, index, savings: toNumericAmount(item?.totalSavings) }))
    .sort((a, b) => (b.savings - a.savings) || (a.index - b.index))
    .map(entry => entry.item);
}

/**
 * Replaces the state tokens in the "Why this strategy" text with the state
 * name. The tokens are "$$state$$" and "0 state". Missing text gives ''.
 * An empty or blank state name keeps the text unchanged, with the tokens.
 */
export function applyStateName(suggestedInfo: string | null | undefined, stateName: string): string {
  if (!suggestedInfo) return '';
  if (!stateName || !stateName.trim()) return suggestedInfo;
  return suggestedInfo.split('$$state$$').join(stateName).split('0 state').join(stateName);
}

/**
 * Reads a plan amount such as 6000, "6000" or "$6,000". Returns null when the
 * value is missing, blank or not a number, for example "N/A". A 0 stays 0.
 */
export function parsePlanAmount(value: any): number | null {
  if (typeof value === 'number') {
    return isFinite(value) ? value : null;
  }
  const text = String(value ?? '').replace(/[$,]/g, '').trim();
  if (text === '') return null;
  const parsed = Number(text);
  return isFinite(parsed) ? parsed : null;
}

export interface DiscoveryWorkingSetInput {
  ocrData: any;
  planningYear: any;
  strategies: any[] | null | undefined;
  stateName: string;
  /** Formats a number for display, for example numeral '$0,0'. */
  formatAmount: (amount: number) => string;
}

/**
 * Builds the RAP_DISCOVERY_WORKING_SET envelope for the first Discovery prompt.
 * It lists all strategies in Top 5 order. Each strategy has a rank, a top-five
 * flag, the plan amount and an amount disposition. An unparseable plan amount
 * gives null and 'needs_calculation'.
 */
export function buildDiscoveryWorkingSet(input: DiscoveryWorkingSetInput): any {
  const { ocrData, planningYear, strategies, stateName, formatAmount } = input;
  return {
    contextType: WORKING_SET_CONTEXT_TYPE,
    schemaVersion: WORKING_SET_SCHEMA_VERSION,
    planningYear: planningYear ?? null,
    ocrData,
    strategies: rankByDisplayedTop5(strategies).map((strategy: any, index: number) => {
      const planAmount = parsePlanAmount(strategy.planAmount);
      return {
        rank: index + 1,
        isTopFive: index + 1 <= TOP_FIVE_COUNT,
        strategyId: strategy.id,
        name: strategy.name,
        planAmount,
        planAmountDisplay: planAmount === null ? null : formatAmount(planAmount),
        amountDisposition: planAmount === null ? 'needs_calculation' : 'engine_calculated',
        estimatedSavings: strategy.totalSavings,
        suggestedInfo: applyStateName(strategy.suggestedInfo, stateName),
        amountDescriptorText: strategy.fieldDescriptor
      };
    })
  };
}
